import {useEffect, useState} from 'react';
import type {Component, Editor} from 'grapesjs';
import type {ImageComponentType} from '@Libraries/email-campaign-ai-image-regeneration-library';
import {getImageTypeCounts, getTextCount} from '@Libraries/ai-email-campaign-content-library';

export interface EmailEditorComponentCounts {
  textCount: number;
  imageTypeCounts: Record<ImageComponentType, number>;
}

export function useEmailEditorComponentCounts(editor: Editor): EmailEditorComponentCounts {
  const [imageTypeCounts, setImageTypeCounts] = useState<Record<ImageComponentType, number>>(() => getImageTypeCounts(editor));
  const [textCount, setTextCount] = useState<number>(() => getTextCount(editor));

  useEffect(() => {
    let scheduledFrame = 0;

    const recountFromEditor = (): void => {
      setImageTypeCounts(getImageTypeCounts(editor));
      setTextCount(getTextCount(editor));
    };

    const scheduleRecount = (): void => {
      window.cancelAnimationFrame(scheduledFrame);
      scheduledFrame = window.requestAnimationFrame(recountFromEditor);
    };

    const scheduleRecountIfRelevant = (component: Component): void => {
      const isPMWImage = component.is(window.PMW.emails.components.graphic.PMW_IMAGE);
      const isPMWDesign = component.is(window.PMW.emails.components.graphic.PMW_DESIGN);
      const isPMWText = component.is(window.PMW.emails.components.text.PMW_TEXT);

      const bgImageStyle = component.getStyle()?.['background-image'];
      const hasBg = typeof bgImageStyle === 'string' && bgImageStyle.includes('url(');

      if (!isPMWImage && !isPMWDesign && !hasBg && !isPMWText) {
        return;
      }

      scheduleRecount();
    };

    editor.on('component:add', scheduleRecountIfRelevant);
    editor.on('component:remove', scheduleRecount);
    editor.on('component:styleUpdate:background-image', scheduleRecount);
    editor.on('undo', scheduleRecount);
    editor.on('redo', scheduleRecount);

    return (): void => {
      window.cancelAnimationFrame(scheduledFrame);
      editor.off('component:add', scheduleRecountIfRelevant);
      editor.off('component:remove', scheduleRecount);
      editor.off('component:styleUpdate:background-image', scheduleRecount);
      editor.off('undo', scheduleRecount);
      editor.off('redo', scheduleRecount);
    };
  }, [editor]);

  return {textCount, imageTypeCounts};
}
